import React from 'react'
import useSWR from 'swr'
import '@aws-amplify/ui-react/styles.css'
import { useNavigate } from 'react-router-dom'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { signIn, signOut, fetchAuthSession, getCurrentUser as getCognitoCurrentUser, signUp, resetPassword, confirmResetPassword, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'

import { getCurrentUser } from '../../api/users'
import { User } from '../../api/users/types'
import { AppError } from '../errors/AppError'
import FullScreenError from '../../ui/components/errors/FullScreenError'
import Loading from '../../ui/components/layout/Loading'
import { getAvailableAccess, loginSuccess, syncUser } from '../../api/auth'
import { useI18N } from '../i18n/hook'
import config from '../../config'

Amplify.configure({
	Auth: {
		Cognito: {
			userPoolId: config.VITE_COGNITO_USER_POOL_ID,
			userPoolClientId: config.VITE_COGNITO_APP_ID,
		},
	}
})

export interface AuthContextType {
  checkIsAuthenticated: () => Promise<boolean>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; code?: string }>;
  logout: () => Promise<void>;
  signup: (username: string, password: string, email: string) => Promise<{
    success: boolean;
    message?: string;
    error?: string
  }>;
  forgotPassword: (username: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  confirmPasswordReset: (username: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  verifyEmail: (username: string, code: string) => Promise<{ success: boolean; error?: string }>;
  resendVerificationCode: (username: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)


export const CustomAuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const { t } = useI18N()
  useEffect(() => {
    checkIsAuthenticated()
  }, [])

  const checkIsAuthenticated = async () => {
    try {
      await getCognitoCurrentUser()
      setIsAuthenticated(true)
      return true
    } catch (error) {
      setIsAuthenticated(false)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    try {
      const output = await signIn({ username, password })
      if (output.isSignedIn) {
        await loginSuccess()
        const hasUserSynched = await syncUser()
        if (hasUserSynched) {
          await forceTokenRefresh()
        }
        setIsAuthenticated(true)
        return { success: true }
      } else if(output.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return { success: false, code: 'UserNotConfirmedException' }
      }
      return { success: false, error: output.nextStep?.signInStep }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred during login',
        code: error && typeof error === 'object' && 'code' in error ? (error as { code: string }).code : undefined
      }
    }
  }

  const signup = async (username: string, password: string) => {
    try {
      const availableAccess = await getAvailableAccess(username)
      if (!availableAccess) {
        return {
          success: false,
          error: t('auth.signup.notAllowed')
        }
      }

      await signUp({
        username,
        password,
        options: {
          userAttributes: {
            // We add tenantId as a custom attribute to the user
            'custom:tenant_id': availableAccess.tenantId?.toString() || '0'
          }
        }
      })

      return {
        success: true,
        message: t('auth.signup.success')
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred during signup'
      }
    }
  }

  const logout = async () => {
    try {
      await signOut()
      setIsAuthenticated(false)
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const forgotPassword = async (username: string) => {
    try {
      await resetPassword({ username })
      return {
        success: true,
        message: t('auth.resetPassword.message')
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      }
    }
  }

  const confirmPasswordReset = async (username: string, confirmationCode: string, newPassword: string) => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword
      })
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      }
    }
  }

  const verifyEmail = async (username: string, code: string) => {
    try {
      await confirmSignUp({
        username,
        confirmationCode: code
      })
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify email'
      }
    }
  }

  const resendVerificationCode = async (username: string) => {
    try {
      await resendSignUpCode({ username })
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resend verification code'
      }
    }
  }

  const value = {
    checkIsAuthenticated,
    isAuthenticated,
    isLoading,
    login,
    logout,
    signup,
    forgotPassword,
    confirmPasswordReset,
    verifyEmail,
    resendVerificationCode
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook for easy auth context usage
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface LinkedinProfile { sub: string, givenName: string, familyName: string }
interface ICurrentUserContext {
  currentUser?: User
  refetchCurrentUser: () => Promise<void>
  isLinkedinConnected?: boolean
  linkedinProfile?: LinkedinProfile
  setLinkedinStorage: (data: unknown) => void
  removeLinkedinStorage: () => void
  isSubscriptionActive?: boolean
}
export const CurrentUserContext = React.createContext<ICurrentUserContext>({ refetchCurrentUser: async() => {}, removeLinkedinStorage : () => {}, setLinkedinStorage: () => {}})
type Props = { children: React.ReactNode }
export const CurrentUserProvider: React.FC<Props> = ({ children }) => {
	const { t } = useI18N()
	const { data: currentUser, error, isLoading, mutate } = useSWR('/api/users', getCurrentUser, { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateOnMount: true })
	if(isLoading) {
		return <Loading size='screen' message={t('common.loadingUser')} />

	} else if (error) {

		return <FullScreenError error={new AppError(`${t('common.errorFetchingUser')} ${error.message}`)} />

	}

  const setLinkedinStorage = (data: unknown) => { sessionStorage.setItem('linkedinUser', JSON.stringify(data)) }
  const removeLinkedinStorage = () => { sessionStorage.removeItem('linkedinUser') }
  const getLinkedinStorage = () => { return sessionStorage.getItem('linkedinUser') ? JSON.parse(sessionStorage.getItem('linkedinUser')!) : null }
  const isLinkedinConnected = !!currentUser?.linkedinSub || !!getLinkedinStorage()

	if(currentUser?.needsTokenRefresh) {
		forceTokenRefresh()
	}

  const refetchCurrentUser = async () => { await mutate() }
	return (
    <CurrentUserContext.Provider value={{
      currentUser,
      isSubscriptionActive: currentUser?.products && currentUser?.products.length > 0,
      refetchCurrentUser,
      isLinkedinConnected,
      linkedinProfile: getLinkedinStorage(),
      setLinkedinStorage,
      removeLinkedinStorage
    }}
    >
      {children}
    </CurrentUserContext.Provider>
  )
}

export enum ACCESS_TYPES {
  FREE = 'free',
  WORDPRESS = 'wordpress'
}

/**
	 * Force cognito token refresh.
	 * This is usually used when cognito user was synched with our db data.
	 */
const forceTokenRefresh = async () => {
	try {
		await fetchAuthSession({ forceRefresh: true })
	} catch (error) {
	console.error('Error refreshing token:', error)
	}
}
